// `chrome-extension.extension-tabs.shortlist.hunter.edit-alert` — the alert criteria builder
// (plan §7 phase 3, slice 2).
//
// ⚠️ THIS HEADER USED TO SAY "ONE SECTION OF NINE" AND IT WAS STALE FOR MONTHS, which is worth
// more than a correction: the plan's §7 slice 2 still calls `edit-alert` "untouched", and a
// status report was written off both. All nine sections are here, save and prediction are
// wired, and both routes are pixel-verified — `/shortlist/edit-alert` at 0.14%,
// `/client-contact/edit-alert` at 0.02%. `search-form/` is 8.8k lines behind a 1104-line
// template, so the size was real; the "untouched" was not. Check the route before trusting a
// note about it.
import { useEffect, useMemo, useRef, useState, type ReactElement } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { ExtensionViewWidth } from '@woo-ext/protocol'
import {
  AlertEntity,
  NEW_ALERT_ID,
  emptyAlert,
  fetchViewModel,
  getAtsFiltersConfiguration,
  getAtsFiltersSingleselectValues,
  getExtensionCriteriaPrediction,
  saveAlert,
  searchAtsJobsByTitle,
  getTitleRecommendations,
  type TitleRecommendation,
  getJobTitles,
  getExtensionSkills,
  getExtensionIndustries,
  getCities,
  getLocationsOrRegionsFull,
  getAlerts,
  getExternalSourceConfiguration,
  type ExtensionAlertContainer,
  type AtsFiltersFieldConfiguration,
  type AtsFilterValue,
  type ExtensionRegionDetails,
  type ExternalSourceConfiguration,
  type ExtensionCriteriaPrediction,
} from '@woo-ext/api'
import {
  AlertEditFrame,
  AlertTabState,
  ConfirmChangesDialogue,
  type AlertTabPrediction,
  AtsFiltersSection,
  atsFiltersHaveErrors,
  type AtsFilterRow,
  loadAtsFilters,
  emptyFieldOfExpertiseRow,
  emptyKeywordCondition,
  emptySkillRow,
  LATEST_CANDIDATE_CRITERIA_VERSION,
  mapEducation,
  mapFieldOfExpertise,
  mapKeywords,
  mapSkills,
  mapWorkExperience,
  loadContactDetails,
  loadEducation,
  loadExternalSourceFilters,
  loadFieldOfExpertise,
  loadKeywords,
  loadLocation,
  loadSkills,
  loadWorkExperience,
  type SavedCriteria,
  isAtsFilterDataValid,
  isExternalSourceFilterValid,
  pruneEmptyCriteria,
  ExternalSourceFiltersSection,
  type ExternalSourceFilterRow,
  seedExternalSourceFilters,
  SOCIAL_HOST_DISPLAY_NAME,
  SOURCE_LOGO,
  criteriaNavigationOptions,
  CriteriaNavigation,
  CriteriaBuilderShell,
  CriteriaFormState,
  ExtensionAlertsHost,
  ExtensionAlertTab,
  usedNavigationOptions,
  FieldOfExpertiseOperator,
  FieldOfExpertiseSection,
  ContactDetailsSection,
  ANY_DEGREE_GROUP,
  ANY_FIELD_GROUP,
  DEGREE_GROUPS,
  DEGREE_GROUPS_FOR_DISPLAY,
  EducationSection,
  FIELD_GROUPS,
  FIELD_GROUPS_FOR_DISPLAY,
  INSTITUTE_BADGES,
  KeywordSearchSection,
  LocationSection,
  regionsForCountry,
  mapLocation,
  SkillSetSection,
  WorkExperienceSection,
  type FieldOfExpertiseRow,
  type ContactDetailsCriteria,
  type EducationCriteria,
  type KeywordCondition,
  type LocationCriteria,
  type SkillCriteriaRow,
  type WorkExperienceCriteria,
  LogicalOperator,
  getAtsLabel,
} from '@woo-ext/ui'
import { ExtensionProduct, removeLastAlertViewed } from './lastAlertViewed.ts'

function useAlertId(): number | undefined {
  const [params] = useSearchParams()
  const raw = params.get('alertId')
  if (raw === null) return undefined
  const parsed = Number.parseInt(raw, 10)
  return Number.isNaN(parsed) ? undefined : parsed
}

export function EditAlert(): ReactElement {
  const alertId = useAlertId()

  // The edit state's `onEnter`: "always unset lastAlertViewedState when entering edit alert (if
  // last alert viewed is the edited alert)" — an edited search matches different rows, so the
  // stored ids would replay a result set that no longer exists.
  useEffect(() => {
    if (alertId === undefined) return
    void removeLastAlertViewed(ExtensionProduct.Candidates, alertId)
  }, [alertId])
  const navigate = useNavigate()
  const [containers, setContainers] = useState<ExtensionAlertContainer[] | undefined>(undefined)
  const [linkedJobEnabled, setLinkedJobEnabled] = useState(false)
  const [linkedJobText, setLinkedJobText] = useState('')
  const [linkedJobId, setLinkedJobId] = useState<number | null | undefined>(undefined)
  const [jobSuggestions, setJobSuggestions] = useState<ReadonlyArray<{ id: number; autocompleteText: string }>>([])
  /**
   * `foe.titleRecommendations`, per row. Fetched when a row's titles change — upstream calls
   * `getTitleRecommendationsOnOtherPositionAddedRemoved` from exactly that event, and cancels
   * the previous request for the row rather than letting two answers race.
   */
  const [titleRecommendations, setTitleRecommendations] = useState<Record<number, readonly TitleRecommendation[]>>({})
  const [recommendationsLoading, setRecommendationsLoading] = useState(false)
  /**
   * `getJobAutocompleteText` — `${title}${atsHasJobCode ? ' ' + internalTitle : ''}${
   * atsHasConcreteEmployers ? ' - ' + employerName : ''}`.
   *
   * ⚠️ BOTH SEGMENTS ARE WHITELABEL FLAGS, not "show it if present". A tenant without job
   * codes gets the title alone even when the DTO carries an `internalTitle`, so keying on the
   * field's presence puts a code on screen that production hides.
   */
  const [atsHasJobCode, setAtsHasJobCode] = useState(false)
  const [atsHasConcreteEmployers, setAtsHasConcreteEmployers] = useState(false)
  // ⚠️ `atsFiltersCriteriaEnabled` IS HARDCODED `true` UPSTREAM
  // (`search.page.component.ng1.ts:416`), so the nav condition
  // `atsFiltersCriteriaEnabled && configuration.fields.length > 0` reduces to the field count
  // alone — which is what `hasAtsFilters` passes. A previous note here claimed the
  // configuration call was unported and the entry fell back to `atsName`; both halves were
  // wrong, and the fallback would have shown one entry too many on a tenant with an ATS and no
  // filterable fields.
  // The ATS LABEL (`getAtsLabel`, 5cfd3ef899): every use on this screen is a title.
  const [atsLabel, setAtsLabel] = useState<string | undefined>(undefined)
  // `exitEdit()`'s confirmation — open only while the dialogue is up.
  const [confirmingExit, setConfirmingExit] = useState(false)
  const [hasExternalSources, setHasExternalSources] = useState(false)
  // `tempExtensionCriteria` upstream — the criteria being edited, which is a COPY the form
  // mutates and only writes back on save. One row to start with, as `addFOE` leaves it.
  const [fieldOfExpertise, setFieldOfExpertise] = useState<FieldOfExpertiseRow[]>([{}])
  // (the seeding effect replaces these once the bootstrap lands — see `bootstrapReady`)
  const [operator, setOperator] = useState<LogicalOperator | null>(null)
  const [skills, setSkills] = useState<SkillCriteriaRow[]>([{}])
  const [workExperience, setWorkExperience] = useState<WorkExperienceCriteria>({})
  const [education, setEducation] = useState<EducationCriteria>({})
  const [contactDetails, setContactDetails] = useState<ContactDetailsCriteria>({})
  const [keywords, setKeywords] = useState<KeywordCondition[]>([{}])
  const [location, setLocation] = useState<LocationCriteria>({})
  const [regionsByCountry, setRegionsByCountry] = useState<Record<string, ExtensionRegionDetails[]>>({})
  // ⚠️ ONE EMPTY ROW, NOT NONE. `initAtsFilters(null)` returns `[{}]`, so the table shows a
  // Field select waiting to be filled even on an alert with no saved filters — which is what
  // the section looks like at rest, and 69px of the screen.
  const [atsFilterRows, setAtsFilterRows] = useState<AtsFilterRow[]>([{}])
  const [atsFilterFields, setAtsFilterFields] = useState<AtsFiltersFieldConfiguration[]>([])
  const [singleselectValues, setSingleselectValues] = useState<Record<string, AtsFilterValue[]>>({})
  const [externalSources, setExternalSources] = useState<ExternalSourceConfiguration[]>([])
  // Keyed by host site, because the section is one independent table PER SOURCE.
  const [externalSourceFilters, setExternalSourceFilters] = useState<Record<string, ExternalSourceFilterRow[]>>({})
  // The note that stood here said these came from `window.careero.degrees` and were "not on
  // the bootstrap yet". They are not bootstrap data at all —
  // `extension-education-types-mapping.service.ng1.ts` declares all three as literals in its
  // own body — so the columns rendered their "Any" row alone and the section was eight
  // checkboxes short of production. See `educationGroups.ts`.
  // `isCompanyNameSearchEnabled` — `featureFlags.COMPANY_NAME_SEARCH_ENABLED`
  // (search.page.component.ng1.ts:419). It gates the Company name block inside Work
  // experience, which is 74px of the form, so guessing it wrong shifts every section below.
  const [companyNameSearchEnabled, setCompanyNameSearchEnabled] = useState(false)
  // `extendedTitleClassFeatureEnabled` and the UX flag ANDED with it — see the section's props.
  const [extendedTitleClass, setExtendedTitleClass] = useState(false)
  const [extendedTitleClassUX, setExtendedTitleClassUX] = useState(false)
  // ⚠️ `versionId` IS WHY A BLANK FORM STILL PREDICTS. `mapTempCriteriaToExtensionCriteria`
  // includes it unconditionally and `isEmpty(4)` is false, so the pruned criteria object is
  // NEVER empty — `getPrediction`'s `Object.keys(...).length === 0` guard therefore never fires
  // on a real alert, and the tab shows a count rather than its spinner. Dropping the field
  // because nothing renders it inverts that guard and leaves the spinner up for good.
  const [versionId, setVersionId] = useState<number | undefined>(undefined)
  // The criteria as they were LOADED, so `isCriteriaChanged` can be answered honestly.
  const loadedCriteria = useRef<string | undefined>(undefined)
  const [saving, setSaving] = useState(false)
  /**
   * ⚠️ SAVE MUST NOT RUN BEFORE THE FORM HOLDS THE ALERT'S CRITERIA, and `bootstrapReady`
   * opened exactly that window: `getAlerts` can answer before the view model, which leaves
   * `current` pointing at a real saved search while every section still holds its empty
   * defaults. A Save in that interval writes those defaults OVER the user's criteria — the
   * same data loss this file's other guards exist to prevent, introduced by the fix for
   * them. The ref beside it cannot do this job: a ref does not re-render, so the handler
   * would read a stale value.
   */
  const [seededAlertId, setSeededAlertId] = useState<number | undefined>(undefined)
  /**
   * `editedExtensionAlert.name` — the rename, held here because the frame only draws it.
   *
   * ⚠️ `undefined` MEANS "NOT RENAMED", NOT "EMPTY". Seeding it from the loaded alert would
   * pin the first render's value, and the alert arrives asynchronously — the field would
   * start blank and stay blank. The fallback is read at use, so an untouched form saves the
   * name it loaded with.
   */
  const [editedName, setEditedName] = useState<string | undefined>(undefined)

  const [prediction, setPrediction] = useState<AlertTabPrediction | undefined>(undefined)
  // The criteria as last SENT, so the debounce can tell a real change from a re-render.
  const lastPredicted = useRef<string | undefined>(undefined)
  // `ctrl.fieldTypes = $window.careero.extensionFieldTypes` and `pathPerField` likewise
  // (search.page.component.ng1.ts:374). Both already travel on `/extension/view-model`, which
  // is exactly what Phase 1 extracted the bootstrap for.
  const [fieldTypes, setFieldTypes] = useState<ReadonlyArray<{ enum: string; name: string }>>([])
  const [pathPerField, setPathPerField] = useState<
    Readonly<Record<string, ReadonlyArray<{ value: string; display: string }>>>
  >({})
  const [canViewGenericSearch, setCanViewGenericSearch] = useState(false)
  const [otherFieldsFieldName, setOtherFieldsFieldName] = useState<string | undefined>(undefined)
  /**
   * ⚠️ THE SEED HAS TO WAIT FOR THE VIEW MODEL, and not waiting cost the whole section.
   * `loadFieldOfExpertise` builds the blank row through `emptyFieldOfExpertiseRow`, which only
   * applies the "Other fields" sentinel when `extendedTitleClass` is on — and on a NEW alert
   * the seeding effect fires on the first render, before `fetchViewModel` has answered, so the
   * row was built with the flag still `false` and no `field`. The effect's `loadedAlertId`
   * guard then refused to re-seed when the flags landed a moment later, and
   * `mapFieldOfExpertise` drops every row whose `field` is unset: measured against production
   * build 354, every field-of-expertise criterion was absent from our wire while production
   * sent two rows.
   *
   * Set on failure as well as success — a view model that never answers must not leave the
   * form permanently unseeded.
   */
  const [bootstrapReady, setBootstrapReady] = useState(false)

  useEffect(() => {
    let live = true
    // The ATS name titles one section and the source configuration decides whether another
    // exists at all — but neither blocks the rest: the seven unconditional sections render
    // immediately, as upstream's do.
    fetchViewModel().then(
      (model) => {
        if (!live) return
        setAtsLabel(getAtsLabel(model))
        setFieldTypes(model.extensionFieldTypes ?? [])
        setPathPerField(model.extensionPathPerField ?? {})
        setRegionsByCountry(model.extensionRegionDetailsByCountry ?? {})
        setAtsHasJobCode(model.atsHasJobCode === true)
        setAtsHasConcreteEmployers(model.atsHasConcreteEmployers === true)
        // `otherFieldsFieldName` is the SENTINEL the "Other fields" option stores, and it comes
        // from the bootstrap rather than being a literal — a hardcoded 'OTHER' would be a
        // constant this port invented for a value the server owns.
        // ⚠️ THE NAME ALONE DOES NOT ARM THE SENTINEL — `extendedTitleClass`, set from the
        // feature flag below, is what decides whether a new row carries it. See
        // `emptyFieldOfExpertiseRow`.
        setOtherFieldsFieldName(model.otherFieldsFieldName ?? undefined)
        setBootstrapReady(true)
        // `canViewGenericSearch() = featureFlags[GENERIC_SEARCH] || admin`
        // (search.page.component.ng1.ts:2007). The flag half is read here; the admin half needs
        // the role plumbing and is deliberately not guessed at — an admin on a tenant without
        // the flag sees one option fewer than upstream, which is a smaller wrong than showing
        // it to everyone.
        setCanViewGenericSearch(model.featureFlags?.['GENERIC_SEARCH'] === true)
        // ⚠️ THE LINKED-JOB BLOCK IS BEHIND A FLAG **AND** THE PRODUCT. Upstream writes
        // `isLinkedJobEnabled && (!extensionProduct || extensionProduct === Candidates)`, so
        // the client-contact builder never shows it even where the flag is on. The product
        // half is the route, and this is the candidate route.
        setLinkedJobEnabled(model.featureFlags?.['LINKED_JOB_ENABLED'] === true)
        setCompanyNameSearchEnabled(model.featureFlags?.['COMPANY_NAME_SEARCH_ENABLED'] === true)
        // ⚠️ THE SECOND IS ANDED WITH THE FIRST (search.page.component.ng1.ts:412), not read on
        // its own: the UX variant only exists inside the extended-title feature.
        const extended = model.featureFlags?.['EXTENDED_TITLE_CLASS_SEARCH_CLIENT_SIDE'] === true
        setExtendedTitleClass(extended)
        setExtendedTitleClassUX(extended && model.featureFlags?.['EXTENDED_TITLE_CLASS_SEARCH_UX'] === true)
      },
      () => {
        // ⚠️ THE GATE OPENS ON FAILURE TOO — see the note on `bootstrapReady`. A view model
        // that never answers must not leave the form permanently unseeded; the flags keep
        // their defaults and the sections seed from the alert as before.
        setBootstrapReady(true)
      },
    )
    getAlerts(AlertEntity.Candidates).then(
      (loaded) => {
        if (live) setContainers(loaded)
      },
      () => undefined,
    )
    // ⚠️ THE FIELD COUNT, NOT JUST THE RESPONSE, DECIDES THE SECTION. Upstream's condition is
    // `atsFiltersCriteriaEnabled && configuration.fields.length > 0`, so a tenant whose ATS is
    // configured but has no filterable fields gets no section and no nav entry.
    getAtsFiltersConfiguration().then(
      (configuration) => {
        if (!live) return
        setAtsFilterFields(configuration.fields)
        // ⚠️ THE OPTIONS ARE FETCHED FOR EVERY CONFIGURED SINGLE-SELECT SECTION AT ONCE, ONCE
        // — not per row when a user picks one. Upstream reads them straight off the
        // configuration it just received and stops if there are none, so a tenant with no
        // single-select fields makes no second call at all. Fetching per row instead would
        // work and would issue one request per selection.
        const sections = configuration.fields
          .filter((field) => field.type === 'SINGLE_SELECT')
          .map((field) => field.section)
        if (sections.length === 0) return
        getAtsFiltersSingleselectValues(sections, 'CANDIDATE').then(
          (values) => {
            if (live) setSingleselectValues(values)
          },
          () => undefined,
        )
      },
      () => undefined,
    )
    getExternalSourceConfiguration().then(
      (response) => {
        if (!live) return
        // ⚠️ THE SECTION FILTERS ON `configured`; THE SOURCES DROPDOWN DOES NOT. Upstream's
        // `externalSources = configurations.filter(c => c.configured)` here, against the
        // dropdown's straight map over the same array — two different rules for one response,
        // and using the dropdown's here shows filter tables for sources the tenant has not set
        // up. `hasExternalSources` stays on the unfiltered length, because the collapsible's
        // own `ng-if` is `configurations.length > 0`.
        setHasExternalSources(response.configurations.length > 0)
        const configured = response.configurations.filter((configuration) => configuration.configured)
        setExternalSources(configured)
        setExternalSourceFilters((previous) =>
          seedExternalSourceFilters(
            configured.map((configuration) => configuration.hostSite),
            previous,
          ),
        )
      },
      () => undefined,
    )
    return () => {
      live = false
    }
  }, [])

  // ⚠️ `NEW_ALERT_ID` HAS TO BRANCH BEFORE THE FALLBACK, not after. `-1` matches no container,
  // so the `?? containers?.[0]` below would resolve "Add search" to the user's FIRST saved
  // search and seed the form with its criteria — a new alert pre-filled with someone else's,
  // saved under a new id. Memoised because `current` is the form-seeding effect's dependency:
  // a fresh object each render re-seeds on every keystroke and the screen cannot be typed in.
  const blank = useMemo<ExtensionAlertContainer>(() => ({ id: NEW_ALERT_ID, alert: emptyAlert() }), [])
  const current =
    alertId === NEW_ALERT_ID
      ? blank
      : (containers?.find((container) => container.alert.id === alertId) ?? containers?.[0])
  // ⚠️ THE SECTIONS SEED FROM THE ALERT, ONCE, AND ONLY WHEN IT CHANGES. `initEditedExtensionAlert`
  // copies the criteria into the form on load; re-running it on every render would overwrite
  // whatever the user has typed with the saved values, which is a bug that only appears while
  // someone is editing. The alert's ID is the dependency, not the object — `getAlerts` returns
  // a fresh array each fetch, so depending on the container would reset the form on any refetch.
  //
  // The two filter sections are merged rather than replaced: the external-source one has to
  // keep its per-source empty rows for sources the alert saved nothing under.
  const loadedAlertId = useRef<number | undefined>(undefined)
  const currentAlertId = current?.alert.id
  useEffect(() => {
    if (!bootstrapReady) return
    if (current === undefined || currentAlertId === undefined) return
    if (loadedAlertId.current === currentAlertId) return
    loadedAlertId.current = currentAlertId

    const saved = (current.alert.extensionCriteria ?? {}) as SavedCriteria
    // ⚠️ A NEW ALERT CARRIES THE LATEST VERSION, NOT NOTHING. `getNewEmptyCriteria()` returns
    // `{ versionId: latestCandidateCriteriaVersionId }` (4), and the service migrates on the
    // way in keyed on that number — so an alert saved without one is not "unversioned", it is
    // a candidate for a migration path that no longer applies. Production sent `versionId: 4`
    // on every call of the comparison run; we sent none.
    setVersionId(saved.versionId ?? LATEST_CANDIDATE_CRITERIA_VERSION)
    loadedCriteria.current = undefined
    setFieldOfExpertise(loadFieldOfExpertise(saved.fieldOfExpertise, otherFieldsFieldName, extendedTitleClass))
    setOperator((saved.fieldOfExpertiseOperator ?? null) as LogicalOperator | null)
    setSkills(loadSkills(saved.skills))
    setWorkExperience(loadWorkExperience(saved))
    // ⚠️ WITH THE GROUPS. `loadEducation(saved)` alone folds against an EMPTY list, so every
    // saved degree and field resolved to `false` and the section opened blank on an alert that
    // had them — the read half of the same defect as the write half above.
    setEducation(loadEducation(saved.education, DEGREE_GROUPS, FIELD_GROUPS))
    setContactDetails(loadContactDetails(saved.contactDetails))
    setLocation(loadLocation(saved.location))
    setKeywords(loadKeywords(saved.keywords))
    setAtsFilterRows(loadAtsFilters(saved.atsFilters))
    const savedExternal = loadExternalSourceFilters(saved.externalSourceSearchCriteriaFilters)
    setExternalSourceFilters((previous) => ({ ...previous, ...savedExternal }))
    setSeededAlertId(currentAlertId)
  }, [bootstrapReady, current, currentAlertId, otherFieldsFieldName, extendedTitleClass])

  /**
   * `handleOtherPositionsFieldOfExpertiseOperator` (`search.page.component.ng1.ts:1048`).
   *
   * ⚠️ WITH THE EXTENDED-TITLE FLAG ON, THE OPERATOR HAS NO CONTROL — AND STILL HAS TO BE SET.
   * `foe-operator-radio-container` is `ng-show="… && !extendedTitleClassFeatureEnabled"`, so
   * the radios never appear on such a tenant; upstream therefore defaults the operator to
   * `ALL` the moment a second row exists. `mapFieldOfExpertise` withholds the ENTIRE section
   * when there is more than one row and no operator, so without this the second row silently
   * deletes the first one's criteria — a second, independent cause of the same disappearance
   * the "Other fields" sentinel caused, and one the sentinel fix alone does not cover.
   * Production sent `fieldOfExpertiseOperator: 0` on exactly this path.
   */
  useEffect(() => {
    if (!extendedTitleClass) return
    if (fieldOfExpertise.length <= 1) return
    if (operator !== null) return
    setOperator(LogicalOperator.All)
  }, [extendedTitleClass, fieldOfExpertise.length, operator])

  const navigationOptions = criteriaNavigationOptions({
    atsLabel,
    hasAtsFilters: atsFilterFields.length > 0,
    hasExternalSources,
  })
  // The criteria object the nav counts against. It is assembled from the sections' own state
  // rather than read off a `tempExtensionCriteria` the form posts, because nothing posts yet —
  // and it is the KEYS that decide the count, which are the keys upstream names.
  // `mapTempCriteriaToExtensionCriteria` — every section mapped, then every EMPTY key deleted.
  //
  // ⚠️ THE TWO FILTER SECTIONS MAP THROUGH THEIR VALIDITY PREDICATE FIRST, and the seeded
  // empty row is what makes that matter. `mapAtsFiltersCriteria` keeps only valid filters and
  // returns null when none survive; the external-source mapper does the same across all
  // sources. Passing the rows through as-is marks both sections used on a blank form, and the
  // left rail reads "2/9" where production reads "0/9".
  const validAtsFilters = atsFilterRows.filter((row) => isAtsFilterDataValid(row.data))
  const validExternalSourceFilters = Object.values(externalSourceFilters)
    .flat()
    .filter((row) => isExternalSourceFilterValid(row))
  const criteria = pruneEmptyCriteria({
    fieldOfExpertise: mapFieldOfExpertise(fieldOfExpertise, operator, otherFieldsFieldName),
    fieldOfExpertiseOperator: operator,
    skills: mapSkills(skills),
    // ⚠️ SPREAD, NOT NESTED. The six work-experience blocks are six TOP-LEVEL keys on the wire;
    // there is no `workExperience` object. The section groups them for the user, the criteria
    // do not.
    ...mapWorkExperience(workExperience),
    // ⚠️ MAPPED, NOT PASSED — the same lesson `location` below learned. The section's
    // checkboxes are keyed by DISPLAY GROUP ("BA / BsC") and the wire wants the folded VALUES
    // (`ba`, `bsc`), so passing the form's object straight through saved a key the server
    // round-trips but `loadEducationSection` cannot read: the alert saved fine and reopened
    // with every box empty.
    // ⚠️ THE FULL LISTS, NOT THE `_FOR_DISPLAY` ONES. Those drop the "Any" group because the
    // section renders it in its own row — but it is a real group with a real value (`any` /
    // `ANY`), and mapping without it would send the literal string "Any degree".
    education: mapEducation(education, DEGREE_GROUPS, FIELD_GROUPS),
    contactDetails,
    // ⚠️ MAPPED, NOT PASSED. The form's `location` is not the DTO's — `regions` is a
    // `Record<name, boolean>` here and an `ArrayList<ExtensionLocationDTO>` there, and sending
    // the form's object made the server answer 400 on EVERY save and on every `getPrediction`,
    // so the edit screen could neither predict nor persist. See `mapLocation`.
    location: mapLocation(location, regionsForCountry(regionsByCountry, location.country)),
    atsFilters: validAtsFilters,
    externalSourceSearchCriteriaFilters: validExternalSourceFilters,
    keywords: mapKeywords(keywords),
    // Carried through untouched — see the note on the state. It is not a criterion and no
    // navigation option claims it, so it does not count toward "Filters used".
    versionId,
  })

  // ⚠️ THE PREDICTION IS DEBOUNCED BY A SECOND, AND AN EMPTY FORM SKIPS THE CALL ENTIRELY.
  // `getPrediction` cancels any pending timer and re-arms a 1s one; the dedupe below is that
  // cancellation, expressed as "the same criteria does not ask twice".
  //
  // The dependency is the criteria's JSON rather than the object, because the object is rebuilt
  // on every render and would retrigger the timer forever.
  const criteriaKey = JSON.stringify(criteria)
  useEffect(() => {
    // ⚠️ THE EMPTY-FORM SKIP WAS A MISREADING, AND IT LEFT A NEW SEARCH SPINNING FOREVER.
    // `getPrediction` does return early on `Object.keys(lastValidExtensionCriteria).length
    // === 0` — but that object is `mapTempCriteriaToExtensionCriteria()`, which is the WIRE
    // shape with every key present and the unused ones null, plus `versionId`. It is never
    // empty. This port prunes the nulls away before counting, so a blank form came out `{}`
    // and the request was never sent. Measured on the same tenant, a brand-new alert: the
    // reference fires `getPrediction` twice and renders "Matched candidates 439", while this
    // sat on the spinner with zero requests — exactly the report.
    if (criteriaKey === lastPredicted.current) return undefined

    let live = true
    const timer = setTimeout(() => {
      lastPredicted.current = criteriaKey
      getExtensionCriteriaPrediction({
        // ⚠️ `isEditingExistingAlert ? alert.id : null` — AND `-1` IS NOT AN ALERT ID. The
        // note here was right and the expression under it was not: `useAlertId()` returns the
        // URL's value, which for a new search is the `NEW_ALERT_ID` sentinel, so `?? null`
        // left it as `-1` and the tenant answered 500 (its own HTML error page) while the
        // same body with `null` answers `{results: 439}`. Measured both ways against the
        // tenant: null, 122 and a pruned body all return 200; only `-1` fails.
        alertId: alertId === undefined || alertId === NEW_ALERT_ID ? null : alertId,
        extensionCriteria: JSON.parse(criteriaKey) as Record<string, unknown>,
        // `getFiltersForCountersAndPrediction`. `atsFilters` is the ALERT's own saved filters,
        // which the DTO carries at top level and this screen does not read yet.
        filter: { showHiddenCandidates: false },
      }).then(
        (answer: ExtensionCriteriaPrediction) => {
          if (live) setPrediction({ prediction: answer.results, topMatches: answer.topMatches })
        },
        () => undefined,
      )
    }, 1000)
    return () => {
      live = false
      clearTimeout(timer)
    }
  }, [criteriaKey, alertId])

  // The first criteria object assembled after a load IS the loaded state — the sections have
  // just been seeded from the alert and nothing has been typed. Recording it here rather than
  // mapping the saved criteria a second time keeps one definition of the wire shape.
  if (loadedCriteria.current === undefined) loadedCriteria.current = criteriaKey

  /**
   * `saveAlert` — ⚠️ `isCriteriaChanged` IS NOT "the form is dirty". The server RE-RUNS THE
   * SEARCH when it is true, so a pure rename must send false; upstream computes it from a JSON
   * comparison against the criteria as loaded (`isCriteriaJsonChanged`), which is what the ref
   * above holds.
   */
  /**
   * `hasChanges()` — `extensionAlert.id === NEW_ALERT_ID || isFormDirty()`.
   *
   * ⚠️ A BRAND-NEW ALERT IS ALWAYS DIRTY, EVEN UNTOUCHED, and that is the half a reasonable
   * implementation drops. Upstream short-circuits on the sentinel id before asking the form
   * anything, because leaving a new alert loses it whether or not a field was typed into —
   * there is nothing on the server to go back to.
   *
   * The dirty test itself reuses the same JSON comparison `isCriteriaChanged` does, so the
   * form cannot be dirty for the dialogue and clean for the save in the same breath.
   */
  // ⚠️ KEYED ON THE TITLES, NOT ON THE ROWS. The rows object is rebuilt on every keystroke in
  // any field; the titles are what the recommendation actually depends on.
  const titleKey = JSON.stringify(fieldOfExpertise.map((row) => (row.otherPositions ?? []).map((tag) => tag.text)))
  useEffect(() => {
    const perRow = JSON.parse(titleKey) as string[][]
    let live = true
    setRecommendationsLoading(perRow.some((titles) => titles.length > 0))
    void Promise.all(
      perRow.map(async (titles) => (titles.length === 0 ? [] : getTitleRecommendations(titles).catch(() => []))),
    ).then((answers) => {
      if (!live) return
      setTitleRecommendations(Object.fromEntries(answers.map((rows, index) => [index, rows])))
      setRecommendationsLoading(false)
    })
    return () => {
      live = false
    }
  }, [titleKey])

  /** `getJobAutocomplete($viewValue)` — debounced, and silent on an empty field. */
  const jobSearchSeq = useRef(0)
  useEffect(() => {
    if (!linkedJobEnabled || linkedJobText.trim() === '') {
      setJobSuggestions([])
      return undefined
    }
    const seq = jobSearchSeq.current + 1
    jobSearchSeq.current = seq
    const timer = setTimeout(() => {
      void searchAtsJobsByTitle(linkedJobText).then(
        (found) => {
          if (jobSearchSeq.current !== seq) return
          setJobSuggestions(
            found.map((job) => ({
              id: job.id,
              autocompleteText: `${job.title}${atsHasJobCode && job.internalTitle != null ? ` ${job.internalTitle}` : ''}${
                atsHasConcreteEmployers && job.employerName != null ? ` - ${job.employerName}` : ''
              }`,
            })),
          )
        },
        () => {
          if (jobSearchSeq.current === seq) setJobSuggestions([])
        },
      )
    }, 300)
    return () => {
      clearTimeout(timer)
    }
  }, [linkedJobText, linkedJobEnabled, atsHasJobCode, atsHasConcreteEmployers])

  const hasChanges = (): boolean => current?.alert.id === NEW_ALERT_ID || criteriaKey !== loadedCriteria.current

  const exit = (): void => {
    if (current === undefined) return
    void navigate(`/chrome-extension/extension/shortlist/review?alertId=${String(current.alert.id)}`)
  }

  const onSave = (): void => {
    if (current === undefined || saving) return
    // See `seededAlertId`: saving a form that has not taken the alert's criteria yet would
    // overwrite them with its defaults.
    if (seededAlertId !== current.alert.id) return
    const name = editedName ?? current.alert.name
    // `if (!this.isFormValid || !this.editedExtensionAlert.name) return` — a nameless alert is
    // not saved, and the button simply does nothing rather than reporting an error.
    if (name === undefined || name === '') return

    setSaving(true)
    saveAlert(AlertEntity.Candidates, {
      id: current.alert.id,
      name,
      extensionCriteria: criteria,
      isCriteriaChanged: criteriaKey !== loadedCriteria.current,
      // The picked job wins; untouched, the alert keeps the one it had.
      linkedJobId: linkedJobId === undefined ? (current.alert.linkedJobId ?? null) : linkedJobId,
    }).then(
      (updated) => {
        setSaving(false)
        // The saved criteria become the new baseline, so a second Save without edits sends
        // `isCriteriaChanged: false` rather than re-running the search again.
        loadedCriteria.current = criteriaKey
        setContainers((previous) => {
          const kept = (previous ?? []).filter(
            (container) => container.alert.id !== updated.alert.id && container.alert.id !== NEW_ALERT_ID,
          )
          return [...kept, updated]
        })
        // ⚠️ SAVE NAVIGATES AWAY, AND THE EARLIER READING OF `onSaveAlert` STOPPED ONE BLOCK
        // SHORT. `entity-hunters-container-base.ng1.ts:624` ends with an UNCONDITIONAL
        // `$state.go(Review, { alertId, autoShowResults: true })`; the `externalSource` branch
        // above it returns, which is what made it look like the screen stays put. It does not
        // — pressing Save and remaining on the form is the defect, and on a NEW alert it is
        // worse than cosmetic: the form still holds the `-1` sentinel, so a second Save
        // creates a SECOND alert.
        //
        // `autoShowResults` is a declared boolean param on the review state, so it belongs in
        // the URL even while nothing here reads it — the route string is the BI contract.
        void navigate(
          `/chrome-extension/extension/shortlist/review?alertId=${String(updated.alert.id)}&autoShowResults=true`,
        )
      },
      () => {
        // Upstream surfaces failures through the shortlist's error dialogue, which has not been
        // ported; leaving the form as it was is the honest behaviour until it has, because the
        // one thing that must not happen is the screen claiming a save that did not occur.
        setSaving(false)
      },
    )
  }

  return (
    // ⚠️ `minWidth`, NOT `flexBasis` — the edit state sets `min-width: ExtensionViewWidth.Full`
    // and lets the panel grow, where the review state pins a 200px `flex-basis`.
    <ExtensionAlertsHost entity="candidates" minWidth={ExtensionViewWidth.Full}>
      <AlertEditFrame
        alertName={editedName ?? current?.alert.name}
        newAlert={current?.alert.id === NEW_ALERT_ID}
        onAlertNameChange={setEditedName}
        linkedJobEnabled={linkedJobEnabled}
        linkedJobText={linkedJobText}
        onLinkedJobTextChange={(value) => {
          setLinkedJobText(value)
          // `typeahead-editable="false"` — typing CLEARS the link rather than keeping the
          // last picked job under a name that no longer matches it.
          setLinkedJobId(null)
        }}
        linkedJobSuggestions={jobSuggestions}
        onLinkedJobSelect={(job) => {
          setLinkedJobText(job.autocompleteText)
          setLinkedJobId(job.id)
        }}
        // The SECOND Save/Cancel, at the foot of the form — see `AlertEditFrame`. Same two
        // handlers the tab's pair uses: upstream binds both rows to `saveAlert()` and
        // `exitEdit()`.
        onSave={onSave}
        onCancel={() => {
          if (hasChanges()) {
            setConfirmingExit(true)
            return
          }
          exit()
        }}
        tab={
          current === undefined ? null : (
            <ExtensionAlertTab
              alert={current.alert}
              state={AlertTabState.Edit}
              currentAlertId={alertId}
              {...(prediction === undefined ? {} : { prediction })}
              onSave={onSave}
              onCancel={() => {
                // `exitEdit()` — confirm first when the form is dirty, leave at once when it
                // is not. See `hasChanges` below for what "dirty" means here.
                if (hasChanges()) {
                  setConfirmingExit(true)
                  return
                }
                exit()
              }}
            />
          )
        }
        navigation={
          <CriteriaNavigation
            options={navigationOptions}
            used={usedNavigationOptions(navigationOptions, criteria)}
            onNavigate={(option) => {
              // `smoothScroll` with a 400ms duration and an 85px offset upstream; the anchor
              // is the section id either way.
              document.getElementById(option.elementId)?.scrollIntoView({ behavior: 'smooth', block: 'start' })
            }}
          />
        }
      >
        <CriteriaBuilderShell
          state={CriteriaFormState.Shortlist}
          atsLabel={atsLabel}
          hasAtsFilters={atsFilterFields.length > 0}
          hasExternalSources={hasExternalSources}
          sections={{
            'field-of-expertise-criteria-id': (
              <>
                <FieldOfExpertiseSection
                  rows={fieldOfExpertise}
                  fieldTypes={fieldTypes}
                  pathPerField={pathPerField}
                  canViewGenericSearch={canViewGenericSearch}
                  extendedTitleClass={extendedTitleClass}
                  extendedTitleClassUX={extendedTitleClassUX}
                  {...(otherFieldsFieldName === undefined ? {} : { otherFieldsFieldName })}
                  operator={operator}
                  recommendations={titleRecommendations}
                recommendationsLoading={recommendationsLoading}
                // `onSelectTitleRecommendation` — the picked title joins the row's positions.
                onSelectRecommendation={(index, title) => {
                  setFieldOfExpertise((previous) =>
                    previous.map((row, at) =>
                      at === index
                        ? { ...row, otherPositions: [...(row.otherPositions ?? []), { text: title }] }
                        : row,
                    ),
                  )
                }}
                loadTitles={(index, query) =>
                  getJobTitles(
                    query,
                    (fieldOfExpertise[index]?.otherPositions ?? []).map((tag) => tag.text),
                    extendedTitleClass,
                  )
                }
                onChange={(index, row) => {
                    setFieldOfExpertise((previous) => previous.map((existing, at) => (at === index ? row : existing)))
                  }}
                  onRemove={(index) => {
                    // ⚠️ THE LAST ROW IS RESET, NOT REMOVED. Every one of upstream's four
                    // removers branches on `length === 1` and puts the EMPTY row back
                    // (`removeFOE`, `removeSkillCriteria`,
                    // `removeKeywordCriteriaCondition`, `removeAtsFilterCriteria`); only a
                    // second row is spliced out. Filtering unconditionally left the section
                    // with no row at all, so the controls vanished and the only way back was
                    // the Add button — measured on the ATS section, where production keeps a
                    // "Please select..." field and this showed none.
                    setFieldOfExpertise((previous) =>
                      previous.length === 1
                        ? [emptyFieldOfExpertiseRow(otherFieldsFieldName, extendedTitleClass)]
                        : previous.filter((_, at) => at !== index),
                    )
                  }}
                  onAdd={() => {
                    // ⚠️ UPSTREAM'S EMPTY ROW, NOT `{}`. `addFOE` pushes
                    // `getEmptyTempFieldOfExpertiseCriteria()`, which carries `when:
                    // Recently` and — with the flag on — the "Other fields" sentinel. A bare
                    // object differs on both counts: production's second row defaulted to
                    // "Last 3 years" where ours showed "Any time", and without the sentinel
                    // the row never reaches the wire at all.
                    setFieldOfExpertise((previous) => [...previous, emptyFieldOfExpertiseRow(otherFieldsFieldName, extendedTitleClass)])
                  }}
                />
                {/* Outside the criteria table, and only once a second row exists — see the
                component's note. */}
                <FieldOfExpertiseOperator rows={fieldOfExpertise.length} operator={operator} onChange={setOperator} />
              </>
            ),
            'skills-criteria-id': (
              <SkillSetSection
                rows={skills}
                canViewGenericSearch={canViewGenericSearch}
                loadSkills={(query) => getExtensionSkills(query)}
                onChange={(index, row) => {
                  setSkills((previous) => previous.map((existing, at) => (at === index ? row : existing)))
                }}
                onRemove={(index) => {
                  // See the field-of-expertise note: the last row is reset, not removed.
                  setSkills((previous) => (previous.length === 1 ? [emptySkillRow()] : previous.filter((_, at) => at !== index)))
                }}
                onAdd={() => {
                  // `getEmptyTempSkillCriteria()` — `lastUsed: AnyTime` and
                  // `allowUnknownYears: false`, both of which production sends on a second row
                  // and a bare object omits.
                  setSkills((previous) => [...previous, emptySkillRow()])
                }}
              />
            ),
            'work-experience-criteria-id': (
              <WorkExperienceSection
                criteria={workExperience}
                companyNameSearchEnabled={companyNameSearchEnabled}
                loadIndustries={(query) => getExtensionIndustries(query)}
                onChange={setWorkExperience}
              />
            ),
            'education-criteria-id': (
              <EducationSection
                criteria={education}
                anyDegreeGroup={ANY_DEGREE_GROUP}
                degreeGroups={DEGREE_GROUPS_FOR_DISPLAY}
                anyFieldGroup={ANY_FIELD_GROUP}
                fieldGroups={FIELD_GROUPS_FOR_DISPLAY}
                instituteBadges={INSTITUTE_BADGES}
                onChange={setEducation}
              />
            ),
            'location-criteria-id': (
              <LocationSection
                criteria={location}
                // ⚠️ THE THREE CITY FIELDS ARE THREE DIFFERENT CALLS, not one. The
                // distance-from field passes `allowNullCountry` so it works before a country
                // is chosen; the Israel-only city field does not; and the global field is a
                // different endpoint that answers regions as well as cities.
                loadDistanceCities={(query) => getCities(query, location.country, { allowNullCountry: true })}
                loadCities={(query) => getCities(query, location.country)}
                loadGlobalLocations={(query) => getLocationsOrRegionsFull(query, location.country)}
                regions={regionsForCountry(regionsByCountry, location.country)}
                onChange={setLocation}
              />
            ),
            'contact-details-criteria-id': (
              <ContactDetailsSection criteria={contactDetails} onChange={setContactDetails} />
            ),
            'ats-filters-criteria-id': (
              <AtsFiltersSection
                rows={atsFilterRows}
                fields={atsFilterFields}
                singleselectValues={singleselectValues}
                // `ctrl.form.atsFilters.$error.validAtsFilters` — see `atsFiltersHaveErrors`.
                showErrors={atsFiltersHaveErrors(atsFilterRows)}
                onChange={(index, row) => {
                  setAtsFilterRows((previous) => previous.map((existing, at) => (at === index ? row : existing)))
                }}
                onRemove={(index) => {
                  // `removeAtsFilterCriteria` — `atsFilters[0] = {}` when it is the only one.
                  setAtsFilterRows((previous) => (previous.length === 1 ? [{}] : previous.filter((_, at) => at !== index)))
                }}
                onAdd={() => {
                  setAtsFilterRows((previous) => [...previous, {}])
                }}
              />
            ),
            'external-source-filters-criteria-id': (
              <ExternalSourceFiltersSection
                sources={externalSources.map((configuration) => ({
                  hostSite: configuration.hostSite,
                  displayName: SOCIAL_HOST_DISPLAY_NAME[configuration.hostSite],
                  // `ng-if="!!icon"` upstream — three of the six hosts have one.
                  ...(SOURCE_LOGO[configuration.hostSite] === undefined
                    ? {}
                    : { iconUrl: SOURCE_LOGO[configuration.hostSite] }),
                  fields: configuration.criteriaFilters ?? [],
                }))}
                rowsBySource={externalSourceFilters}
                onChange={(hostSite, rows) => {
                  setExternalSourceFilters((previous) => ({ ...previous, [hostSite]: rows }))
                }}
              />
            ),
            // Rendered only when the shell is `Shortlist` — the section table gates it, so passing
            // it here unconditionally is correct and costs nothing standalone.
            'keywords-criteria-id': (
              <KeywordSearchSection
                conditions={keywords}
                onChange={(index, condition) => {
                  setKeywords((previous) => previous.map((existing, at) => (at === index ? condition : existing)))
                }}
                onRemove={(index) => {
                  setKeywords((previous) =>
                    previous.length === 1 ? [emptyKeywordCondition()] : previous.filter((_, at) => at !== index),
                  )
                }}
                onAdd={() => {
                  // `getEmptyTempKeywordCriteriaCondition()` — the default category is the
                  // enum's LAST member and the condition is `IncludeAny`; production's second
                  // condition carried both and ours carried neither.
                  setKeywords((previous) => [...previous, emptyKeywordCondition()])
                }}
              />
            ),
          }}
        />
      </AlertEditFrame>
      {/* ⚠️ RENDERED ALWAYS, OPEN CONDITIONALLY — and `UnifiedModal` returns null while it is
          closed, so this still costs no pixels on the reference capture. (It used to be a
          native `<dialog>`, which is what gave the confirmation a black border and a
          viewport-centred box; see the note there.) */}
      <ConfirmChangesDialogue
        open={confirmingExit}
        onConfirm={() => {
          setConfirmingExit(false)
          exit()
        }}
        onCancel={() => {
          setConfirmingExit(false)
        }}
      />
    </ExtensionAlertsHost>
  )
}
